/* eslint-disable react/prop-types */
import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2, XCircle, Loader2, Search, AlertTriangle,
} from "lucide-react";
import api from "@/lib/api";

/**
 * Campo de RNC con lookup contra el padrón DGII cacheado en Mongo.
 * - Al escribir 9 dígitos (RNC) hace lookup automático con debounce (400ms).
 * - 11 dígitos → cédula (no se valida contra DGII).
 * - Muestra badge:
 *     verde si ACTIVO,
 *     amarillo si SUSPENDIDO (no bloquea),
 *     rojo si no existe en DGII.
 * - Invoca `onResolve(info)` para auto-rellenar el nombre.
 */
export default function RncLookupField({ value, onChange, onResolve, testid }) {
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("idle");   // idle|loading|valid|suspended|notfound|cedula
  const [err, setErr] = useState("");
  const debRef = useRef(null);

  const clean = (v) => String(v || "").replace(/[-\s]/g, "");

  const doLookup = async (v) => {
    const c = clean(v);
    if (!c) { setState("idle"); setInfo(null); return; }
    if (c.length === 11) { setState("cedula"); setInfo(null); return; }
    if (c.length !== 9) { setState("idle"); setInfo(null); return; }
    setState("loading"); setErr("");
    try {
      const { data } = await api.get(`/dgii/rnc/lookup/${c}`);
      setInfo(data);
      const estado = String(data?.estado || "").toUpperCase();
      setState(estado === "SUSPENDIDO" ? "suspended" : "valid");
      onResolve?.(data);
    } catch (e) {
      setInfo(null);
      setState("notfound");
      setErr(e.response?.data?.detail || "RNC no encontrado en DGII");
    }
  };

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => doLookup(value), 400);
    return () => debRef.current && clearTimeout(debRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className="relative mt-1">
      <div className="flex gap-2 items-center">
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="RNC (9) o Cédula (11)"
          data-testid={testid}
          className="pr-9"
        />
        <Button type="button" variant="ghost" size="icon"
                className="shrink-0" onClick={() => doLookup(value)}
                data-testid={`${testid}-check`} title="Verificar en DGII">
          {state === "loading" ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Search className="w-4 h-4 text-muted-foreground" />
          )}
        </Button>
      </div>

      {state === "valid" && info && (
        <div className="mt-1.5 flex items-center gap-2 text-xs">
          <Badge className="bg-success/15 text-success border-success/30">
            <CheckCircle2 className="w-3 h-3 mr-1" /> {info.estado || "ACTIVO"}
          </Badge>
          <span className="text-foreground font-medium truncate">
            {info.razon_social}
          </span>
          {info.regimen && (
            <span className="text-muted-foreground">· {info.regimen}</span>
          )}
        </div>
      )}
      {state === "suspended" && info && (
        <div className="mt-1.5 space-y-1">
          <div className="flex items-center gap-2 text-xs">
            <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30">
              <AlertTriangle className="w-3 h-3 mr-1" /> {info.estado}
            </Badge>
            <span className="text-foreground font-medium truncate">
              {info.razon_social}
            </span>
          </div>
          <p className="text-xs text-amber-600">
            Este contribuyente está suspendido en DGII. Podrás guardarlo,
            pero DGII podría rechazar e-CF emitidos a su favor.
          </p>
        </div>
      )}
      {state === "notfound" && (
        <div className="mt-1.5 flex items-start gap-2 text-xs text-destructive">
          <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{err}. Para facturar sin régimen DGII, deja el RNC vacío (genérico) o marca como <strong>Extranjero/Informal</strong>.</span>
        </div>
      )}
      {state === "cedula" && (
        <div className="mt-1.5 text-xs text-muted-foreground">
          Cédula de 11 dígitos: no se valida contra DGII (persona física).
        </div>
      )}
    </div>
  );
}


/**
 * Autocomplete por nombre contra el padrón DGII.
 * Mínimo 3 chars, debounce 350ms. Dropdown con resultados.
 * Al seleccionar, invoca `onPick({rnc, razon_social, estado})`.
 */
export function NameFromDgiiField({ value, onChange, onPick, testid }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debRef = useRef(null);

  const doSearch = async (q) => {
    if (!q || q.trim().length < 3) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/dgii/rnc/search`,
        { params: { q, limit: 8 } });
      setResults(data.items || []);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => doSearch(value), 350);
    return () => debRef.current && clearTimeout(debRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className="relative">
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => value?.length >= 3 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        placeholder="Nombre o Razón social"
        data-testid={testid}
        autoComplete="off"
      />
      {loading && (
        <Loader2 className="w-4 h-4 animate-spin absolute right-2 top-2.5 text-muted-foreground" />
      )}
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-64 overflow-y-auto"
             data-testid={`${testid}-suggestions`}>
          {results.map((r) => (
            <button
              type="button"
              key={r.rnc}
              className="w-full text-left px-3 py-2 hover:bg-accent transition-colors border-b border-border/40 last:border-0"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPick?.(r); setOpen(false); }}
              data-testid={`${testid}-opt-${r.rnc}`}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-primary">{r.rnc}</span>
                <span className="text-sm font-medium truncate flex-1">
                  {r.razon_social}
                </span>
                {r.estado && r.estado.toUpperCase() !== "ACTIVO" && (
                  <Badge className="bg-amber-500/15 text-amber-600 text-[9px] uppercase">
                    {r.estado}
                  </Badge>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
